import CommonButton from "@components/CommonButton";
import MemberPopoverContent from "@components/MemberPopoverContent";
import { IconArrow, IconInputDel } from "@icons/index";
import { Input, Popover } from "antd";
import { t } from "i18next";
import { Trans } from "react-i18next";
import { IconAiGradient, IconCredit, IconLoading } from "@icons/index";
import classNames from "classnames";
import { themeActionAtom } from "@src/jotai/theme";
import { useAtomValue } from "jotai";
import { useState } from "react";
import { message } from "antd";
import aiStudioService from "@apis/aiStudioService";
import { useNavigate } from "react-router-dom";
import { useAppSelector } from "@hooks/redux";
import { useMembershipLevel } from "@hooks/membership";
import report from "@report";
import { pushGtmEvent } from "@utils/gtm";
import { GtmEvent } from "@utils/gtmEvents";

/** 去掉链接开头的 http:// 或 https://，用于输入框展示 */
export const stripProtocol = (url: string) => url.replace(/^https?:\/\//i, "").trim();

/** 提交时确保链接带 https:// */
export const ensureHttps = (url: string) => {
  const trimmed = url.trim();
  if (!trimmed) return trimmed;
  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
};

interface InsightProps {
  videoInsight: any;
  handleSearch: (inputWorld: string) => void;
  loading?: boolean;
  defaultKeyword?: string;
}

const Insight = ({ videoInsight, handleSearch, loading, defaultKeyword }: InsightProps) => {
  const isDark = useAtomValue(themeActionAtom) === "dark";
  const [inputWorld, setInputWorld] = useState<string>(defaultKeyword || "");
  const [tryExampleLoading, setTryExampleLoading] = useState(false);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const navigate = useNavigate();
  const { totalRemain } = useAppSelector((state) => state.globalReducer.creditInfo);
  const { isBasicUp } = useMembershipLevel();

  const getPopoverContent = () => {
    if (loading || videoInsight.status === "loading") return null;
    if (!isBasicUp) return <MemberPopoverContent desc="Upgrade to use the feature" action="Upgrade" />;
    if (totalRemain < 1) return <MemberPopoverContent remainCredit={totalRemain} action="Credits" />;
    return (
      <div className="flex gap-[2px] justify-center items-center p-[12px] text-kds-fg text-[14px] font-normal">
        <span className="mr-[2px]">
          <Trans>Cost</Trans>
        </span>
        <IconCredit width={16} height={16} />
        <span className="font-semibold">1</span>
      </div>
    );
  };

  const btnLocked = !isBasicUp || totalRemain < 1;

  const handleSubmit = () => {
    pushGtmEvent(GtmEvent.GoToCreateScriptWithLink);
    if (btnLocked) {
      setPopoverOpen(true);
      return;
    }
    report.click({ kalo_type: "commit_video_by_link" });
    handleSearch(ensureHttps(inputWorld));
  };

  return (
    <div className="h-screen relative">
      <div className="h-full flex flex-col items-center justify-center">
        <div className="h-[437px] flex flex-col items-center">
          <div className={classNames("text-[32px] font-bold", isDark ? "kalo-text-linear-gray" : "text-kds-fg")}>
            <Trans>Video to Storyboard</Trans>
          </div>
          <div className="text-[14px] font-normal text-kds-fg-soft mt-[8px] mb-[40px]">
            <Trans>Turn videos into scripts and master filming secrets.</Trans>
          </div>
          <div className="relative w-[800px]">
            {loading || videoInsight.status === "loading" ? (
              <div
                className="w-[700px] h-[12px] left-0 right-0 mx-auto bottom-[6px] absolute rounded-full blur-[20px] animate-[marquee_3s_linear_infinite]"
                style={{
                  // background: "linear-gradient(90deg, #ff0080, #ff8c00, #40e0d0, #00bfff, #9400d3, #ff0080)",
                  background: "linear-gradient(90deg, #0191F8 42.47%, #CF88FE 87.23%)",
                  backgroundSize: "200% 100%",
                }}
              />
            ) : (
              <div
                className="w-[700px] h-[12px] left-0 right-0 mx-auto bottom-[6px] absolute rounded-full blur-[20px]"
                style={{
                  background: "linear-gradient(90deg, #0191F8 42.47%, #CF88FE 87.23%)",
                  backgroundSize: "100% 100%",
                }}
              />
            )}
            <Input
              value={inputWorld}
              placeholder={t("Paste a TikTok, Kalodata, or YouTube Shorts video link.")}
              size="large"
              prefix={
                <div className="font-semibold text-[14px] text-kds-fg flex items-center pl-[4px]">
                  <span>Https://</span>
                  <div className="w-px h-[40px] bg-kds-border-solid ml-[8px] mr-[4px]"></div>
                </div>
              }
              allowClear={{ clearIcon: <IconInputDel className="w-[16px] h-[16px]" /> }}
              disabled={loading || videoInsight.status === "loading"}
              suffix={
                <Popover
                  content={getPopoverContent()}
                  trigger="hover"
                  open={popoverOpen || undefined}
                  onOpenChange={(open) => setPopoverOpen(open)}
                >
                  <CommonButton
                    autoPath={false}
                    type="primary"
                    className="w-[40px] h-[40px] rounded-[8px]"
                    disabled={btnLocked || loading || videoInsight.status === "loading"}
                    icon={
                      <>
                        {loading || videoInsight.status === "loading" ? (
                          <IconLoading className="animate-spin icon" />
                        ) : (
                          <IconArrow invert className="w-[32px] h-[32px] rotate-90 [&_path]:stroke-current" />
                        )}
                      </>
                    }
                    onClick={handleSubmit}
                  />
                </Popover>
              }
              onKeyDown={(e) => {
                if ((e.code === "Enter" || e.code === "NumpadEnter") && e.keyCode !== 229) {
                  e.preventDefault();
                  handleSubmit();
                }
              }}
              onChange={(e) => {
                setInputWorld(stripProtocol(e.target.value));
              }}
            />
          </div>
          <div
            className={classNames(
              "w-[800px] text-kds-danger mt-[4px] text-[14px] font-normal",
              videoInsight.status === "failed" ? "" : "invisible",
            )}
          >
            <span className="mr-[4px] font-bold">*</span>
            {videoInsight.error}
          </div>
          <div
            className={classNames(
              "text-[14px] font-normal text-kds-fg-soft mt-[15px] flex flex-center gap-[4px] cursor-pointer",
              tryExampleLoading && "pointer-events-none opacity-70",
            )}
            onClick={async () => {
              report.click({ kalo_type: "try_an_example" });
              setTryExampleLoading(true);
              if (tryExampleLoading) return;
              try {
                const data: any = await aiStudioService.fetchExampleVideo();
                if (data?.videoId != null && data?.videoSource != null) {
                  const params = new URLSearchParams({ id: data.videoId, videoSource: data.videoSource });
                  if (data.country) params.set("country", data.country);
                  navigate(`/insight?${params.toString()}`);
                } else {
                  message.error(t("Failed to load example"));
                }
              } catch {
                message.error(t("Failed to load example"));
              } finally {
                setTryExampleLoading(false);
              }
            }}
          >
            <IconAiGradient />
            <Trans>Try an Example</Trans>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Insight;
